// src/screens/DevDiagnostics.jsx
import React, { useState, useEffect } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import {
  loadVerbProgress,
  ensureVerbStatesForWeeklySet,
} from "../data/verbProgressStorage";
import { labelForLanguage } from "../data/languages";
import { TARGET_WORDS, PACE_YEARS } from "../data/learningConfig";


// Wie viele Wochen du aktuell unterstützt (wie im MainMenu)
const MAX_WEEKS = 3;

export default function DevDiagnostics({
  styles,
  onBack,
  language,
  pace,
  profileId,
}) {
  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";

  const years = PACE_YEARS[paceValue] ?? PACE_YEARS.medium;
  const weeks = Math.round(years * 52);
  const weeklyWords = Math.ceil(TARGET_WORDS / weeks);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");

  const [weeklyCountUser, setWeeklyCountUser] = useState(0);
  const [weeklyCountConfig, setWeeklyCountConfig] = useState(0);
  const [progressCount, setProgressCount] = useState(0);
  const [hasSettings, setHasSettings] = useState(false);

  const [verbDebugMessage, setVerbDebugMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadDiagnostics() {
      setLoading(true);
      setError("");

      try {
        // 1) aktuellen User holen
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        if (userError || !userData?.user?.id) {
          if (!cancelled) {
            setError(
              "Kein eingeloggter Benutzer gefunden: " +
                (userError?.message || "")
            );
            setLoading(false);
          }
          return;
        }

        const uid = userData.user.id;
        const mail = userData.user.email || "";
        if (!cancelled) {
          setUserId(uid);
          setEmail(mail);
        }

        // 2) Abfragen parallel ausführen – alles als normale SELECTs
        const [
          weeklyUserRes,
          weeklyConfigRes,
          progressRes,
          settingsRes,
        ] = await Promise.all([
          // a) Alle Weekly-Sets dieses Users (egal welche Config)
          supabase
            .from("weekly_sets")
            .select("user_id")
            .eq("user_id", uid),

          // b) Weekly-Set für aktuelle Config
          supabase
            .from("weekly_sets")
            .select("user_id")
            .eq("user_id", uid)
            .eq("profile_id", profile)
            .eq("language_id", langId)
            .eq("pace", paceValue)
            .eq("total_words", weeklyWords),

          // c) user_progress für aktuelle Sprache & Profil
          supabase
            .from("user_progress")
            .select("word_id")
            .eq("user_id", uid)
            .eq("profile_id", profile)
            .eq("language_id", langId),

          // d) profile_settings für dieses Profil
          supabase
            .from("profile_settings")
            .select("user_id")
            .eq("user_id", uid)
            .eq("profile_id", profile),
        ]);

        const {
          data: weeklyUserData,
          error: weeklyUserError,
        } = weeklyUserRes;
        const {
          data: weeklyConfigData,
          error: weeklyConfigError,
        } = weeklyConfigRes;
        const {
          data: progressData,
          error: progressError,
        } = progressRes;
        const {
          data: settingsData,
          error: settingsError,
        } = settingsRes;

        const errorMessages = [];
        if (weeklyUserError) {
          errorMessages.push(
            "weekly_sets (alle für User): " +
              (weeklyUserError.message ||
                JSON.stringify(weeklyUserError, null, 2))
          );
        }
        if (weeklyConfigError) {
          errorMessages.push(
            "weekly_sets (aktuelle Config): " +
              (weeklyConfigError.message ||
                JSON.stringify(weeklyConfigError, null, 2))
          );
        }
        if (progressError) {
          errorMessages.push(
            "user_progress (aktuelle Sprache/Profil): " +
              (progressError.message ||
                JSON.stringify(progressError, null, 2))
          );
        }
        if (settingsError) {
          errorMessages.push(
            "profile_settings (aktuelles Profil): " +
              (settingsError.message ||
                JSON.stringify(settingsError, null, 2))
          );
        }

        if (errorMessages.length > 0) {
          console.warn("DevDiagnostics: errors", {
            weeklyUserError,
            weeklyConfigError,
            progressError,
            settingsError,
          });
          if (!cancelled) {
            setError(errorMessages.join(" | "));
          }
        }

        if (!cancelled) {
          setWeeklyCountUser(weeklyUserData?.length ?? 0);
          setWeeklyCountConfig(weeklyConfigData?.length ?? 0);
          setProgressCount(progressData?.length ?? 0);
          setHasSettings((settingsData?.length ?? 0) > 0);
        }
      } catch (e) {
        console.warn("DevDiagnostics: exception:", e);
        if (!cancelled) {
          setError(
            "Unerwarteter Fehler beim Laden der Diagnose-Daten: " +
              (e.message || String(e))
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDiagnostics();

    return () => {
      cancelled = true;
    };
  }, [langId, paceValue, weeklyWords, profile]);


  // --- NEU: Verb-Debug-Handler ---
  const handleShowVerbsForWeek = async (weekIndex) => {
    if (!userId) {
      alert("Kein User geladen – bitte DevDiagnostics neu öffnen.");
      return;
    }

    setVerbDebugMessage(`Lade Verben für Woche ${weekIndex}…`);

    try {
      const { data, error } = await supabase
        .from("weekly_sets")
        .select("words")
        .eq("user_id", userId)
        .eq("profile_id", profile)
        .eq("language_id", langId)
        .eq("pace", paceValue)
        .eq("total_words", weeklyWords)
        .eq("week_index", weekIndex)
        .maybeSingle();

      if (error) {
        console.warn("DevDiagnostics: load weekly_set for verbs failed:", error);
        setVerbDebugMessage(
          `Fehler beim Laden des Weekly-Sets für Woche ${weekIndex}.`
        );
        return;
      }

      const weeklySet = data && Array.isArray(data.words) ? data.words : [];

      if (weeklySet.length === 0) {
        setVerbDebugMessage(
          `Kein Weekly-Set für Woche ${weekIndex} gefunden (words leer).`
        );
        return;
      }

      // bestehenden Verb-Fortschritt aus localStorage holen
      const existingStates = loadVerbProgress(langId, profile, weekIndex);

      // sicherstellen, dass alle Verben aus weeklySet einen State haben
      const { verbStates, verbs } = ensureVerbStatesForWeeklySet(
        weeklySet,
        existingStates
      );

      console.log(
        `[DevDiagnostics] Verben für Woche ${weekIndex}:`,
        verbs
      );
      console.log(
        `[DevDiagnostics] VerbStates für Woche ${weekIndex}:`,
        verbStates
      );

      if (!verbs || verbs.length === 0) {
        setVerbDebugMessage(
          `Weekly-Set für Woche ${weekIndex} enthält keine Verben.`
        );
        return;
      }

      const list = verbs
        .map((v) => {
          const es = v.es || v.infinitive || "";
          const de = v.de || v.translation || "";
          return de ? `${es} – ${de}` : es;
        })
        .join("\n");

      setVerbDebugMessage(
        `Verben für Woche ${weekIndex} (${verbs.length}):\n` + list
      );
    } catch (e) {
      console.warn("DevDiagnostics: handleShowVerbsForWeek exception:", e);
      setVerbDebugMessage(
        `Unerwarteter Fehler beim Laden der Verben für Woche ${weekIndex}: ` +
          (e.message || String(e))
      );
    }
  };

  return (
    <>
      <h1 style={styles.title}>Developer · Diagnose</h1>
      <p style={styles.subtitle}>
        Schneller Überblick über User, Weekly-Sets, Progress & Settings.
      </p>

      <div style={styles.infoBox}>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>E-Mail:</span>
          <span>{email || "–"}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>User-ID:</span>
          <span
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              wordBreak: "break-all",
            }}
          >
            {userId || "–"}
          </span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Profil-ID:</span>
          <span>{profile}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Sprache:</span>
          <span>{labelForLanguage(langId)}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Tempo:</span>
          <span>{paceValue}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Weekly-Target:</span>
          <span>{weeklyWords} Wörter/Woche</span>
        </div>
      </div>

      {loading && <p style={{ marginTop: 12 }}>Lade Diagnose-Daten…</p>}

      {!loading && error && (
        <p style={{ marginTop: 12, color: "var(--fluent-coral-dark)", fontSize: 13 }}>
          {error}
        </p>
      )}

      {!loading && !error && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={styles.infoBox}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Weekly-Sets (Tabelle <code>weekly_sets</code>)
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Gesamt für User:</span>
              <span>{weeklyCountUser}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Für aktuelle Config:</span>
              <span>{weeklyCountConfig}</span>
            </div>
          </div>

          <div style={styles.infoBox}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Progress (Tabelle <code>user_progress</code>)
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>
                Zeilen für aktuelle Sprache & Profil:
              </span>
              <span>{progressCount}</span>
            </div>
          </div>

          <div style={styles.infoBox}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Profile Settings (Tabelle <code>profile_settings</code>)
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Eintrag vorhanden:</span>
              <span>{hasSettings ? "Ja" : "Nein"}</span>
            </div>
          </div>

          {/* NEU: Verb-Debug-Bereich */}
          <div style={styles.infoBox}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Verb-Debug (pro Woche)
            </div>
            <p style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>
              Lädt das Weekly-Set aus <code>weekly_sets.words</code>,
              extrahiert die Verben und zeigt sie hier an.
              Details stehen auch in der Browser-Konsole.
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 8,
              }}
            >
              {Array.from({ length: MAX_WEEKS }, (_, i) => i + 1).map(
                (weekIndex) => (
                  <button
                    key={weekIndex}
                    type="button"
                    className="btn-clickable"
                    style={{
                      ...styles.secondaryButton,
                      padding: "6px 10px",
                      fontSize: 12,
                    }}
                    onClick={() => handleShowVerbsForWeek(weekIndex)}
                  >
                    Verben Woche {weekIndex} anzeigen
                  </button>
                )
              )}
            </div>

            {verbDebugMessage && (
              <pre
                style={{
                  marginTop: 8,
                  padding: 8,
                  borderRadius: 8,
                  border: "1px solid var(--fluent-border)",
                  background: "var(--fluent-fog)",
                  fontSize: 11,
                  maxHeight: 180,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {verbDebugMessage}
              </pre>
            )}
          </div>
        </div>
      )}

      <button
        className="btn-clickable"
        style={{ ...styles.secondaryButton, marginTop: 16 }}
        onClick={onBack}
      >
        Zurück zum Hauptmenü
      </button>
    </>
  );
}
